import { Button, Flex } from "@aws-amplify/ui-react"
import type { UserDataDto } from "@strtmn/sds/types"
import { I18n } from "aws-amplify/utils"
import {
  FaArrowLeft,
  FaArrowUpRightFromSquare,
  FaChevronDown,
} from "react-icons/fa6"
import { Menu, MenuItem, Sidebar, SubMenu } from "react-pro-sidebar"
import { Link, NavLink } from "react-router-dom"
import { isDev } from "../config"
import { UserDataContextHelper } from "../contexts"
import { useAdminMode, useIsAdmin } from "../hooks"
import RefreshUserDataButton from "./RefreshUserDataButton"

import "./MainMenu.css"

interface MainMenuProps {
  userData: UserDataDto | undefined
  activeOrgId?: string
}

interface MainMenuToggleProps {
  toggled: boolean
  onClose: () => void
  onBreakPointChange: (broken: boolean) => void
}

const UserMainMenu = ({ activeOrgId, userData }: MainMenuProps) => {
  if (!userData) {
    console.debug("UserMainMenu: !userData")
  }
  if (!activeOrgId) {
    console.debug("UserMainMenu: !activeOrgId")
  }
  // `tryFindOrg`: a stored `activeOrgId` can be stale (org deleted, access
  // removed), and the menu must not be the thing that breaks over it.
  const activeOrg = UserDataContextHelper.tryFindOrg(activeOrgId, userData)

  return (
    <>
      <Flex className="!px-5 !py-4 !flex-col !gap-2.5">
        {/* TODO: Flex's border-bottom cannot be set using tailwind classes */}
        {isDev && <RefreshUserDataButton />}

        {/* fake drop-down */}
        <Link
          to="/orgs/"
          style={{
            textDecorationLine: "none",
          }}
        >
          <Button className="select-org-button">
            <Flex direction={"row"}>
              {/* Shown without an active org too: this links to the org list,
                  so it is how you pick one. Only the label falls back. */}
              {activeOrg?.name ?? I18n.get("Betrieb wählen")}
              <FaChevronDown />
            </Flex>
          </Button>
        </Link>
      </Flex>

      <Menu
        renderExpandIcon={() => <></>}
        className="border-t border-[#d9d9d9] p-2"
        menuItemStyles={{
          button: {
            // the active class will be added automatically by react router
            // so we can use it to style the active menu item
            [`&.active`]: {
              backgroundColor: "#F6F8FB",
              color: "#25C401",
              borderLeft: "2px solid #25C401",
            },
          },
        }}
      >
        {/* Org-specific entries only with an org: without one they would link
            to `/orgs/undefined/...` and break the page they lead to. A user
            without an org (a pending join request) never has one. */}
        {activeOrgId && (
          <MenuItem component={<NavLink to={`/orgs/${activeOrgId}`} end />}>
            {I18n.get("Übersicht")}
          </MenuItem>
        )}
        <MenuItem component={<NavLink to="/vehicles/" end />}>
          {I18n.get("Maschinen")}
        </MenuItem>
        {activeOrgId && (
          <SubMenu label={I18n.get("Rezeptverwaltung")} defaultOpen>
            <MenuItem
              component={
                <NavLink to={`/orgs/${activeOrgId}/feed-components/`} />
              }
            >
              {I18n.get("Komponenten")}
            </MenuItem>
            <MenuItem
              component={<NavLink to={`/orgs/${activeOrgId}/animal-groups/`} />}
            >
              {I18n.get("Ausladestellen")}
            </MenuItem>
            <MenuItem
              component={<NavLink to={`/orgs/${activeOrgId}/recipes/`} />}
            >
              {I18n.get("Rezepte")}
            </MenuItem>
            <SubMenu label={I18n.get("Statistiken")} defaultOpen>
              <MenuItem
                component={
                  <NavLink to={`/orgs/${activeOrgId}/stats/feedings`} />
                }
              >
                {I18n.get("Fütterungen")}
              </MenuItem>
              <MenuItem
                component={
                  <NavLink to={`/orgs/${activeOrgId}/stats/accuracy`} />
                }
              >
                {I18n.get("Genauigkeit")}
              </MenuItem>
              <MenuItem
                component={<NavLink to={`/orgs/${activeOrgId}/stats/v`} />}
              >
                {I18n.get("Verbrauch")}
              </MenuItem>
            </SubMenu>
          </SubMenu>
        )}
        {activeOrgId && (
          <MenuItem component={<NavLink to={`/orgs/${activeOrgId}/bin`} />}>
            {I18n.get("Papierkorb")}
          </MenuItem>
        )}
        <MenuItem component={<NavLink to="/training/" />}>
          {I18n.get("Training")}
        </MenuItem>
        <MenuItem
          component={<NavLink to="https://strautmann.com/strautmannshop" />}
        >
          {I18n.get("Shop")} <FaArrowUpRightFromSquare className="inline" />
        </MenuItem>
        <MenuItem
          component={<NavLink to="https://strautmann.com/ersatzteilservice" />}
        >
          {I18n.get("Ersatzteile")}{" "}
          <FaArrowUpRightFromSquare className="inline" />
        </MenuItem>
      </Menu>

      <div className="absolute w-full bottom-16">
        <Menu className="border-t border-[#d9d9d9] p-2">
          <MenuItem
            component={<NavLink to="https://strautmann.com/kundendienst" />}
          >
            {I18n.get("Kontakt")}{" "}
            <FaArrowUpRightFromSquare className="inline" />
          </MenuItem>
          <MenuItem
            component={<NavLink to="https://strautmann.com/kundendienst" />}
          >
            {I18n.get("Support")}{" "}
            <FaArrowUpRightFromSquare className="inline" />
          </MenuItem>
        </Menu>
      </div>
    </>
  )
}

const AdminMainMenu = () => {
  const { leaveTarget } = useAdminMode()
  const isAdmin = useIsAdmin()

  return (
    <>
      <Flex className="!px-5 !py-4 !flex-col !gap-2.5">
        {/* TODO: Flex's border-bottom cannot be set using tailwind classes */}
        {isDev && <RefreshUserDataButton />}

        <div>
          <Link
            to={leaveTarget}
            className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors text-gray-700 hover:bg-gray-100"
          >
            <FaArrowLeft className="h-4 w-4" aria-hidden />
            {I18n.get("Zurück")}
          </Link>
        </div>
      </Flex>

      {isAdmin && (
        <Menu
          renderExpandIcon={() => <></>}
          className="border-t border-[#d9d9d9] p-2"
          menuItemStyles={{
            button: {
              // the active class will be added automatically by react router
              // so we can use it to style the active menu item
              [`&.active`]: {
                backgroundColor: "#F6F8FB",
                color: "#25C401",
                borderLeft: "2px solid #25C401",
              },
            },
          }}
        >
          <SubMenu
            label={I18n.get("Admin")}
            // component={<NavLink to="/admin/" end />}
            open={true}
            className="admin"
          >
            <MenuItem component={<NavLink to={`/admin/users`} end />}>
              {I18n.get("Benutzer")}
            </MenuItem>
            <MenuItem component={<NavLink to={`/admin/orgs`} end />}>
              {I18n.get("Betriebe")}
            </MenuItem>
            <MenuItem component={<NavLink to={`/admin/machines`} end />}>
              {I18n.get("Maschinen")}
            </MenuItem>
          </SubMenu>
        </Menu>
      )}
    </>
  )
}

const MainMenu = ({
  activeOrgId,
  userData,
  toggled,
  onClose,
  onBreakPointChange,
}: MainMenuProps & MainMenuToggleProps) => {
  const { inAdmin } = useAdminMode()

  if (!userData) {
    console.debug("MainMenu: !userData")
  }
  if (!activeOrgId) {
    console.debug("MainMenu: !activeOrgId")
  }

  return (
    <Sidebar
      id="main-menu"
      className={`!border-r !border-[#d9d9d9]${inAdmin ? " admin" : ""}`}
      customBreakPoint="1024px"
      toggled={toggled}
      onBackdropClick={onClose}
      onBreakPoint={onBreakPointChange}
    >
      {inAdmin ? (
        <AdminMainMenu />
      ) : (
        <UserMainMenu userData={userData} activeOrgId={activeOrgId} />
      )}
    </Sidebar>
  )
}

export default MainMenu
